import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Send, MessageCircle, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { getAvatarUrl } from '../utils/avatarUrl';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChatPage = () => {
  const { conversationId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
    
    // Check if we need to start a new conversation
    const targetUserId = searchParams.get('user');
    if (targetUserId) {
      startNewConversation(targetUserId);
    }
  }, [searchParams]);

  useEffect(() => {
    if (conversationId) {
      fetchMessages(conversationId);
      markAsRead(conversationId);
      const conv = conversations.find(c => c.conversation_id === conversationId);
      setActiveConversation(conv);
    }
  }, [conversationId, conversations]);

  const markAsRead = async (convId) => {
    try {
      await axios.post(`${API_URL}/api/conversations/${convId}/read`, {}, {
        withCredentials: true
      });
    } catch (error) {
      // Silently fail
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/conversations`, {
        withCredentials: true
      });
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (convId) => {
    try {
      const response = await axios.get(`${API_URL}/api/conversations/${convId}/messages`, {
        withCredentials: true
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const startNewConversation = async (userId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/conversations`,
        { user_id: userId },
        { withCredentials: true }
      );
      navigate(`/chat/${response.data.conversation_id}`, { replace: true });
      fetchConversations();
    } catch (error) {
      console.error('Error starting conversation:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !conversationId) return;

    setSendingMessage(true);
    try {
      await axios.post(
        `${API_URL}/api/conversations/${conversationId}/messages`,
        { content: newMessage },
        { withCredentials: true }
      );
      setNewMessage('');
      fetchMessages(conversationId);
      fetchConversations(); // Update last message in list
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  const getOtherParticipant = (conv) => {
    return conv.participants.find(p => p.user_id !== user?.user_id);
  };

  const handleDeleteConversation = async (convId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API_URL}/api/conversations/${convId}`, { withCredentials: true });
      setConversations(prev => prev.filter(c => c.conversation_id !== convId));
      toast.success('Conversazione eliminata');
    } catch (error) {
      console.error('Error deleting conversation:', error);
      toast.error("Errore nell'eliminazione della conversazione");
    }
  };

  const formatMessageTime = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'HH:mm', { locale: it });
    } catch {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-20 bg-slate-100 rounded-2xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  // Conversation list view
  if (!conversationId) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6 animate-fadeIn" data-testid="chat-list-page">
        <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-slate-900">
          Messaggi
        </h1>

        {conversations.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 border border-slate-100 text-center">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="w-10 h-10 text-slate-400" />
            </div>
            <h3 className="font-outfit text-xl font-medium text-slate-900 mb-2">
              Nessuna conversazione
            </h3>
            <p className="text-slate-500 mb-4">
              Inizia una chat dal profilo di un giocatore
            </p>
            <button
              onClick={() => navigate('/players')}
              className="inline-flex items-center gap-2 bg-sky-400 text-white rounded-full px-6 py-3 font-medium hover:bg-sky-500 transition-all"
            >
              Trova giocatori
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {conversations.map((conv) => {
              const other = getOtherParticipant(conv);
              return (
                <div
                  key={conv.conversation_id}
                  className="bg-white rounded-2xl border border-slate-100 hover:border-sky-200 hover:shadow-md transition-all flex items-center"
                  data-testid={`conversation-${conv.conversation_id}`}
                >
                  <button
                    onClick={() => navigate(`/chat/${conv.conversation_id}`)}
                    className="flex-1 p-4 text-left flex items-center gap-4 min-w-0"
                  >
                    <div className="w-12 h-12 bg-sky-400 rounded-xl flex items-center justify-center text-white font-semibold overflow-hidden flex-shrink-0">
                      {getAvatarUrl(other) ? (
                        <img src={getAvatarUrl(other)} alt={other?.name} className="w-full h-full rounded-xl object-cover" />
                      ) : (
                        other?.name?.charAt(0) || '?'
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">
                        {other?.name || 'Utente'}
                      </p>
                      {conv.last_message && (
                        <p className="text-sm text-slate-500 truncate">
                          {conv.last_message.sender_id === user?.user_id ? 'Tu: ' : ''}
                          {conv.last_message.content}
                        </p>
                      )}
                    </div>
                    {conv.last_message && (
                      <span className="text-xs text-slate-400 flex-shrink-0">
                        {formatMessageTime(conv.last_message.created_at)}
                      </span>
                    )}
                  </button>
                  <div className="pr-3">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          data-testid={`delete-conversation-${conv.conversation_id}`}
                          className="w-9 h-9 flex items-center justify-center rounded-xl text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Eliminare questa conversazione?</AlertDialogTitle>
                          <AlertDialogDescription>
                            La conversazione con {other?.name || 'questo utente'} e tutti i messaggi verranno eliminati definitivamente.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annulla</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={(e) => handleDeleteConversation(conv.conversation_id, e)}
                            className="bg-red-500 hover:bg-red-600"
                          >
                            Elimina
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Chat view
  const otherUser = activeConversation ? getOtherParticipant(activeConversation) : null;

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] sm:h-[calc(100vh-80px)]" data-testid="chat-conversation-page">
      {/* Chat Header */}
      <div className="bg-white border-b border-slate-100 px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate('/chat')}
          className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="w-10 h-10 bg-sky-400 rounded-xl flex items-center justify-center text-white font-semibold overflow-hidden">
          {getAvatarUrl(otherUser) ? (
            <img src={getAvatarUrl(otherUser)} alt={otherUser?.name} className="w-full h-full rounded-xl object-cover" />
          ) : (
            otherUser?.name?.charAt(0) || '?'
          )}
        </div>
        <div className="flex-1">
          <p className="font-medium text-slate-900">{otherUser?.name || 'Utente'}</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
        {messages.length === 0 ? (
          <div className="text-center text-slate-400 py-8">
            Inizia la conversazione!
          </div>
        ) : (
          messages.map((msg) => {
            const isMine = msg.sender_id === user?.user_id;
            return (
              <div
                key={msg.message_id}
                className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[75%] px-4 py-3 ${
                    isMine
                      ? 'bg-sky-400 text-white rounded-2xl rounded-br-md'
                      : 'bg-white text-slate-900 rounded-2xl rounded-bl-md border border-slate-100'
                  }`}
                >
                  <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                  <p className={`text-xs mt-1 ${isMine ? 'text-sky-100' : 'text-slate-400'}`}>
                    {formatMessageTime(msg.created_at)}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <form onSubmit={handleSendMessage} className="bg-white border-t border-slate-100 p-4">
        <div className="flex gap-3">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Scrivi un messaggio..."
            data-testid="message-input"
            className="flex-1 px-4 py-3 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20 transition-all outline-none"
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sendingMessage}
            data-testid="send-message-btn"
            className="w-12 h-12 bg-sky-400 text-white rounded-xl flex items-center justify-center hover:bg-sky-500 transition-colors disabled:opacity-50"
          >
            {sendingMessage ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatPage;
